'use strict';

import { isEmpty, forEach } from 'lodash';
import { removeTime, isEarlier } from 'js-date-helper';

import containerManager from '../../01-helpers/filter-context/container-manager';
import { formatSimpleDate } from '../../01-helpers/utilities/date-formatter';

const
	moduleName = 'date-carousel',
	attributeNames = {
		type: 'data-type',
		days: 'data-days',
		prev: 'data-prev',
		next: 'data-next'
	},
	selectors = {
		base: `.js-${moduleName}`,
		date: `.js-${moduleName}-date`,
		navigation: `.js-${moduleName}-nav`
	},
	dateFormatter = Intl.DateTimeFormat('nl', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	});

/**
 * Date Carousel
 * 
 * Component that shows the selected date.
 * You can navigate 1 day forward or back using the navigation buttons
 * 
 * If selectedDate is today, disable previous navigation button.
 * 
 **/

function bindEvents() {
	forEach(this.navigationElements, navEl => {
		navEl.addEventListener('click', (event) =>
			this._onClickEventHandler(event)
		);
	});

	this.filterContext.onUpdate(({ updatedState }) => {
		if (!isEmpty(updatedState)) {
			this.updateDateLabel(updatedState);
			this._setNavigation();
		}
	});
}

export class DateCarousel {

	constructor(baseElement) {
		this.baseElement = baseElement;
		this.navigationElements = this.baseElement.querySelectorAll(selectors.navigation);
		this.filterContext = containerManager.getContainer();

		this._setup();
	}

	_setup() {
		bindEvents.call(this);
		this._setInitialValue();
		this._setNavigation();
	}

	/* =============================================================== */

	get key() {
		return this.baseElement.getAttribute(attributeNames.type);
	}

	get selectedValue() {
		return this.key in this.filterContext.state
			? this.filterContext.state[this.key]
			: null;
	}

	get userFriendlySelectedValue() {
		if (this.key === 'date') {
			return dateFormatter.format(new Date(this.selectedValue));
		}

		const selectedInput = this.selectedInput;

		if (!selectedInput) {
			return '';
		}

		return selectedInput.hasAttribute(attributeNames.friendlyValue)
			? selectedInput.getAttribute(attributeNames.friendlyValue)
			: selectedInput.value;
	}

	/* =============================================================== */

	_setNavigation() {
		//If selectedDate is currentDate, prevent from navigation further in to the passed.
		const today = removeTime(new Date()),
			selectedDate = removeTime(new Date(this.selectedValue)),
			prevNav = this.baseElement.querySelector(`[${attributeNames.prev}]`);

		if (!prevNav) {
			return;
		}

		prevNav.removeAttribute('disabled');

		if (isEarlier(selectedDate, today)) {
			prevNav.setAttribute('disabled', 'disabled');
			return;
		}
	}

	_setInitialValue() {
		const key = this.key;

		// When we can find a value inside the filter context, we set this
		// as the components initial value. If we can not find any value
		// in the filter context we try to rely on a default value.
		const valueToSet =
			key in this.filterContext.state
				? this.filterContext.state[key]
				: this.baseElement.getAttribute(attributeNames.value);

		if (!valueToSet) {
			return;
		}

		this.updateDateLabel({
			[key]: valueToSet
		});
	}

	updateDateLabel(updatedState) {
		const [keyFromFilters] = Object.keys(updatedState);

		if (this.key !== keyFromFilters) {
			return;
		}

		const dateLabel = this.baseElement.querySelector(selectors.date);

		if (!dateLabel) {
			return;
		}

		dateLabel.innerHTML = this.userFriendlySelectedValue;
	}

	/* =============================================================== */

	_onClickEventHandler(evt) {
		if (!evt.currentTarget) {
			return;
		}

		const days = evt.currentTarget.getAttribute(attributeNames.days),
			key = this.key,

		// When we can find a value inside the filter context, we set this
		// as the components initial value. If we can not find any value
		// in the filter context we try to rely on a default value.
			valueToSet =
			key in this.filterContext.state
				? this.filterContext.state[key]
				: this.baseElement.getAttribute(attributeNames.value);

		const selectedDate = new Date(valueToSet),
			newDate = selectedDate.setDate(selectedDate.getDate() + Number.parseInt(days)),
			formattedDate = formatSimpleDate(newDate, '-');

		this.filterContext.update(
			{
				[this.key]: formattedDate
			},
			{
				refresh: true
			}
		);		
	}
}

/* =============================================================== */

const initDateCarousel = () => {
	forEach(document.querySelectorAll(selectors.base), (element) => {
		new DateCarousel(element);
	});
};

initDateCarousel();
